import { createContext, useCallback, useContext, useMemo, useState, type ReactElement, type ReactNode } from 'react'
import { useQueryState } from 'nuqs'
import { type ConnectionLayerOptions } from 'app/components'
import { parseAsTeamId } from '@util'
import { type CompanyData, isDefined, isNotDefined, type TeamData, type TeamId } from '@terros/common'
import { usePreference } from '@front/model'
import { buildFocusLayout, toNodeSelectAction, toTeamRelations, toVisibilityBoundaries, toVisibleTeamIds } from './util'
import { type TeamFocusLayout } from './types'

type TeamHierarchyContextType = {
  layout: TeamFocusLayout
  teams: TeamData[]
  visibilityBoundaries: Map<TeamId, TeamData>
  visibleTeamIds: Set<TeamId> | undefined
  company: CompanyData
  viewTeam: TeamId | null
  focusTeamId: TeamId | null
  setFocusTeam: (teamId: TeamId) => void
  selectTeam: (teamId: TeamId) => void
  openTeamPanel: (teamId: TeamId) => void
  closeTeamPanel: VoidFunction
  toggleRowExpanded: (rowId: string) => void
  connectionSettings: ConnectionLayerOptions
  withPermissions?: boolean
  canEdit?: boolean
}

const EMPTY_VISIBILITY_BOUNDARIES: Map<TeamId, TeamData> = new Map()

const TeamHierarchyContext = createContext<TeamHierarchyContextType | undefined>(undefined)

type TeamHierarchyProviderProps = {
  children: ReactNode
  teams: TeamData[]
  company: CompanyData
  connectionSettings: ConnectionLayerOptions
  withPermissions?: boolean
  canEdit?: boolean
}

export const TeamHierarchyProvider = ({
  children,
  teams,
  company,
  connectionSettings,
  withPermissions,
  canEdit,
}: TeamHierarchyProviderProps): ReactElement => {
  const [expandedRowIds, setExpandedRowIds] = useState<string[]>([])

  const [viewTeam, setViewTeam] = useQueryState('viewTeam', parseAsTeamId)
  const [focusTeam, setFocusTeam] = useQueryState('focusTeam', parseAsTeamId)

  const focusTeamId = focusTeam ?? viewTeam

  const openTeamPanel = useCallback(
    (teamId: TeamId): void => {
      setFocusTeam(teamId)
      setViewTeam(teamId)
    },
    [setFocusTeam, setViewTeam]
  )

  const closeTeamPanel = useCallback((): void => {
    if (isNotDefined(focusTeam) && isDefined(viewTeam)) setFocusTeam(viewTeam)
    setViewTeam(null)
  }, [focusTeam, viewTeam, setFocusTeam, setViewTeam])

  const selectTeam = useCallback(
    (teamId: TeamId): void => {
      const action = toNodeSelectAction({ teamId, focusTeamId, panelTeamId: viewTeam })
      if (action === 'close') {
        closeTeamPanel()
        return
      }
      if (action === 'open') {
        openTeamPanel(teamId)
        return
      }
      setFocusTeam(teamId)
    },
    [focusTeamId, viewTeam, closeTeamPanel, openTeamPanel, setFocusTeam]
  )

  const toggleRowExpanded = useCallback((rowId: string): void => {
    setExpandedRowIds((prev) => {
      if (prev.includes(rowId)) return prev.filter((id) => id !== rowId)
      return [...prev, rowId]
    })
  }, [])

  const layout = useMemo(
    () => buildFocusLayout({ teams: teams ?? [], focusTeamId, expandedRowIds }),
    [teams, focusTeamId, expandedRowIds]
  )

  const { data: restrictionsEnabled } = usePreference('enableVisibilityRestrictions')
  const visibilityBoundaries = useMemo(
    () => (restrictionsEnabled ? toVisibilityBoundaries(teams ?? []) : EMPTY_VISIBILITY_BOUNDARIES),
    [teams, restrictionsEnabled]
  )

  const relations = useMemo(() => toTeamRelations(teams ?? []), [teams])

  const visibleTeamIds = useMemo(
    () =>
      restrictionsEnabled
        ? toVisibleTeamIds({
            relations,
            boundaries: visibilityBoundaries,
            focusTeamId: layout.focusTeam?.teamId ?? null,
          })
        : undefined,
    [relations, visibilityBoundaries, restrictionsEnabled, layout.focusTeam]
  )

  const contextValue = useMemo(
    () => ({
      layout,
      teams: teams ?? [],
      visibilityBoundaries,
      visibleTeamIds,
      company,
      viewTeam,
      focusTeamId,
      setFocusTeam,
      selectTeam,
      openTeamPanel,
      closeTeamPanel,
      toggleRowExpanded,
      connectionSettings,
      withPermissions,
      canEdit,
    }),
    [
      layout,
      teams,
      visibilityBoundaries,
      visibleTeamIds,
      company,
      viewTeam,
      focusTeamId,
      setFocusTeam,
      selectTeam,
      openTeamPanel,
      closeTeamPanel,
      toggleRowExpanded,
      connectionSettings,
      withPermissions,
      canEdit,
    ]
  )

  return <TeamHierarchyContext.Provider value={contextValue}>{children}</TeamHierarchyContext.Provider>
}

export const useTeamHierarchyContext = (): TeamHierarchyContextType => {
  const context = useContext(TeamHierarchyContext)
  if (context === undefined) {
    throw new Error('useTeamHierarchyContext must be used within a TeamHierarchyProvider')
  }
  return context
}
